/**
 * The module registry: one line per web module. A new module adds its folder with a
 * `module.ts` and one line here — nothing else in the shell changes.
 */
import type { AnyRoute } from '@tanstack/react-router';
import { publicRoute } from '@/app/publicRoute';
import { rootRoute } from '@/app/rootRoute';
import { proposalsInbox } from '@/assistant/inbox';
import { registerInboxSource } from '@/lib/inbox';
import { registerInvalidation } from '@/lib/realtime';
import { activityModule } from './activity/module';
import { adminModule } from './admin/module';
import { automationModule } from './automation/module';
import { coachingModule } from './coaching/module';
import { commsModule } from './comms/module';
import { fleetModule } from './fleet/module';
import { homeModule } from './home/module';
import { insightsModule } from './insights/module';
import { integrationsModule } from './integrations/module';
import { legacyModule } from './legacy/module';
import { liveModule } from './live/module';
import { mediaModule } from './media/module';
import { meModule } from './me/module';
import { placesModule } from './places/module';
import { insuranceModule } from './insurance/module';
import { plannedModule } from './planned/module';
import { qualityModule } from './quality/module';
import { reportsModule } from './reports/module';
import { tachoModule } from './tacho/module';
import type { NavItem } from './types';

export const MODULES = [
  homeModule,
  fleetModule,
  insightsModule,
  reportsModule,
  adminModule,
  tachoModule,
  insuranceModule,
  qualityModule,
  liveModule,
  placesModule,
  activityModule,
  mediaModule,
  coachingModule,
  commsModule,
  meModule,
  automationModule,
  integrationsModule,
  plannedModule,
  legacyModule,
] as const;

/** All module routes, with their types kept for the typed router. */
export const moduleRoutes = [
  ...homeModule.routes,
  ...fleetModule.routes,
  ...insightsModule.routes,
  ...reportsModule.routes,
  ...adminModule.routes,
  ...tachoModule.routes,
  ...insuranceModule.routes,
  ...qualityModule.routes,
  ...liveModule.routes,
  ...placesModule.routes,
  ...activityModule.routes,
  ...mediaModule.routes,
  ...coachingModule.routes,
  ...commsModule.routes,
  ...meModule.routes,
  ...automationModule.routes,
  ...integrationsModule.routes,
  ...plannedModule.routes,
  ...legacyModule.routes,
] as const;

/**
 * Public pages (no sign-in, no shell), under `publicRoute`. Only modules that declare
 * `publicRoutes` are listed here — one line each.
 */
export const publicModuleRoutes = [...reportsModule.publicRoutes, ...commsModule.publicRoutes, ...legacyModule.publicRoutes] as const;

export function allNavItems(): NavItem[] {
  return MODULES.flatMap((m) => m.nav);
}

let done = false;

/** Run each module's setup once (entity tabs, realtime invalidations, entity links). */
export function setupModules(): void {
  if (done) return;
  done = true;
  registerInvalidation('ai_proposal', () => [['ai', 'proposals']]);
  registerInboxSource(proposalsInbox); // the assistant is core: its proposals wait in every inbox
  for (const m of MODULES) m.setup?.();
  if (import.meta.env.DEV) warnMisplacedRoutes();
}

/** A page outside the app listed in `routes` works only by accident: say where it belongs. */
function warnMisplacedRoutes(): void {
  for (const m of MODULES) {
    for (const r of m.routes as readonly AnyRoute[]) {
      const options = r.options as { getParentRoute?: () => AnyRoute; path?: string };
      const parent = options.getParentRoute?.();
      if (parent === rootRoute || parent === publicRoute) {
        console.warn(`[modules] ${m.key}: ${options.path ?? '?'} hangs outside the app — list it in publicRoutes, under publicRoute (@/app/publicRoute).`);
      }
    }
  }
}
